import { PropsWithChildren, useState } from 'react';

import classNames from 'classnames';
import Image from 'next/image';

import { IStory } from 'types/story';

import { StoryTileFooter } from './StoryTileFooter';
import { StoryTileHeader } from './StoryTileHeader';
import { StoryTileLink } from './StoryTileLink';

interface IProps {
  story: IStory;
  showAuthor?: boolean;
  storyUrl: string;
  views?: number;
  preload?: boolean;
}
export const StoryTile = ({
  story,
  showAuthor = true,
  storyUrl,
  children,
  views,
  preload = false,
}: PropsWithChildren<IProps>) => {
  const [showImage, setShowImage] = useState(false);

  return (
    <article>
      <aside>
        <StoryTileHeader story={story} showAuthor={showAuthor} views={views} />
      </aside>

      <StoryTileLink href={storyUrl} className="relative block w-full">
        <div className="overflow-hidden rounded-xl shadow-lg">
          {story.posters?.feed?.fileUrl ? (
            <div className="relative aspect-[9/16] w-full bg-boulderGrey/20">
              <Image
                className={classNames(
                  'block overflow-hidden rounded-xl transition-opacity duration-500',
                  {
                    'opacity-100': showImage,
                    'opacity-0': !showImage,
                  },
                )}
                src={story.posters.feed.fileUrl}
                alt={`Poster for the story "${story.title}"`}
                onLoad={() => setTimeout(() => setShowImage(true), 1)}
                fill
                // Keep in sync with the gallery grid's own breakpoints (GalleryView.tsx /
                // CollectionGalleryView.tsx / tailwind.config.js `portrait-lg` variant, #511):
                // 2 columns below 768px, 3 columns on tablet/portrait-lg, 4 columns on desktop
                // landscape (`sizes` conditions are evaluated in order, first match wins, so the
                // more specific `portrait-lg` condition has to come before the plain `lg:` one).
                sizes="(min-width: 768px) and (orientation: portrait) 33vw, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                preload={preload}
              />
            </div>
          ) : (
            <div className="flex aspect-[9/16] w-full flex-col items-center justify-center bg-boulderGrey/20 px-12">
              <h1 className="mb-6 text-2xl font-bold">{story.title}</h1>
              <p className="text-center">{story.meta.description}</p>
            </div>
          )}
        </div>

        {children}
      </StoryTileLink>

      {story.meta.topicsList.length > 0 && (
        <footer>
          <StoryTileFooter topics={story.meta.topicsList} />
        </footer>
      )}
    </article>
  );
};
